<!--
 * @Author: Victor
 * @Date: 2024-05-06 17:58:57
 * @LastEditors: Merlin
 * @LastEditTime: 2024-07-18 14:31:08
 * @Description: 显示分数和色子的条形组件
-->
<template>
    <div
        class="board-box grid mb-2 grid-cols-3 sm:grid-cols-5 h-[150px] md:h-[125px] sm:h-[125px] rounded-2xl content-center"
        :class="sBgClassName"
    >
        <div class="flex flex-col items-center number-left">
            <p class="text-[55px] font-bold w-[1.2em] text-center">
                {{ progress }}
            </p>
            <span class="text-grey-40 tex-[15px] font-semibold">
                {{ t.GameDice.Prediction.toUpperCase() }}
            </span>
        </div>
        <!-- ↓ 中间的色子 --> 
        <figure class="center-dice relative">
            <!-- ↓ animationDuration 属性看似控制时间，实则影响了速度 -->
            <div id="dice"
                :style="{
                    animationDuration: isSubmiting ? `2s` : '50s',
                }"
            >
                <div class="side front">
                    <div class="dot center"></div>
                </div>
                <div class="side front inner"></div>
                <div class="side top">
                    <div class="dot dtop dleft"></div>
                    <div class="dot dbottom dright"></div>
                </div>
                <div class="side top inner"></div>
                <div class="side right">
                    <div class="dot dtop dleft"></div>
                    <div class="dot center"></div>
                    <div class="dot dbottom dright"></div>
                </div>
                <div class="side right inner"></div>
                <div class="side left">
                    <div class="dot dtop dleft"></div>
                    <div class="dot dtop dright"></div>
                    <div class="dot dbottom dleft"></div>
                    <div class="dot dbottom dright"></div>
                </div>
                <div class="side left inner"></div>
                <div class="side bottom">
                    <div class="dot center"></div>
                    <div class="dot dtop dleft"></div>
                    <div class="dot dtop dright"></div>
                    <div class="dot dbottom dleft"></div>
                    <div class="dot dbottom dright"></div>
                </div>
                <div class="side bottom inner"></div>
                <div class="side back">
                    <div class="dot dtop dleft"></div>
                    <div class="dot dtop dright"></div>
                    <div class="dot dbottom dleft"></div>
                    <div class="dot dbottom dright"></div>
                    <div class="dot center dleft"></div>
                    <div class="dot center dright"></div>
                </div>
                <div class="side back inner"></div>
                <div class="side cover x"></div>
                <div class="side cover y"></div>
                <div class="side cover z"></div>
            </div>
        </figure>

        <!-- ↓ 动态数值（开奖结果） -->
        <div class="flex flex-col items-center number-right">
            <p class="text-[55px] font-bold w-[1.2em] text-center">
                {{ animatedNumber }}
            </p>
            <span class="tex-[15px] font-semibold">
                {{ t.GameDice.LuckyNumber.toUpperCase() }}
            </span>
        </div>
    </div>
</template>


<script setup lang="ts">
import useTranslation from '~/i18n/useTransLation';

const t = useTranslation();
const props = defineProps({
    isUnder: {
        type: Boolean,
        required: false,
        default: true,
    },
    progress: {
        type: Number,
        required: true,
    },
    iAnimDuration: {
        type: Number,
        default: 0,
    },
    isSubmiting: {
        type: Boolean,
        required: true,
    },
    isFast: {
        type: Boolean,
    },
    oGameResult: {
        type: Object,
        default: () => ({}),
    },
    historyMode: Boolean, // 历史窗口模式 
});

// 历史记录窗口中取首值
const animatedNumber = ref(props.oGameResult.rollNumber || '00');
const sBgClassName = ref(
    props.historyMode
    ? (props.oGameResult.winAmount ? 'bg' : 'bgPink')
    : 'bg'
);

// ↓ 数字涨跌动画结束后的回调
const fnAnimEnded = ()=>{
    const {winAmount = 0} = props.oGameResult;
    if (winAmount > 0){
        sBgClassName.value = 'bg';
    }else{
        sBgClassName.value = 'bgPink';
    }
    // ↓ 上报动画播放完毕
    props.oGameResult.resolve(true);
};

watch(
    () => props.oGameResult.ts,
    (newVal, oldVal) => {
        if (!newVal) return;
        const iRollNumber = props.oGameResult.roll_number;
        // console.log("new roll_number:", iRollNumber); 
        const {isFast, iAnimDuration} = props;
        if (isFast) {
            setTimeout(()=>{
                animatedNumber.value = iRollNumber;
                fnAnimEnded();
            }, iAnimDuration);
            return; // 快速模式不要播放以下动画  
        }
        numberIncreasingAnimate(
            animatedNumber.value,
            iRollNumber,
            iAnimDuration - 30,
            animatedNumber,
            fnAnimEnded, 
        );
    },
);
</script>


<style scoped lang="scss">
.board-box{
    background-size: cover;
    background-position: 50%;
}

.bg {
    background: linear-gradient(270deg, rgba(41, 126, 229, 0.75), rgba(22, 31, 44, 0) 60%), url('@/assets/svg/casino/spacedice.svg');
}
.bgPink {
    background: linear-gradient(270deg, rgba(250, 48, 92, 0.75), rgba(22, 31, 44, 0) 60%), url('@/assets/svg/casino/spacedice.svg');
}

/* ↓ 色子容器 */
.center-dice{
    opacity: 1;
}

.number-left,
.number-right{
    > em{
        min-width: 2em;
        text-align: center;
    }
}

@media (max-width: 574px) {
    .number-left,
    .number-right{
        grid-column: span 2;
        > span{
            font-size: 12px;
        }
    }
    .center-dice{
        grid-column: span 1;
        transform: scale(0.85); 
    }
}


#dice {
    animation: spin 50s linear infinite;
    left: 50%;
    margin-left: -30px;
    margin-top: -30px;
    top: 50%;
    transform-style: preserve-3d;
}

#dice,
.side {
    height: 60px;
    position: absolute;
    width: 60px;
}

.side {
    background: #fff;
    border-radius: 12px;
    box-shadow: inset 0 0 12px #ccc;
}

#dice .cover,
#dice .inner {
    background: #e0e0e0;
    box-shadow: none;
}

#dice .cover {
    border-radius: 0;
    transform: translateZ(0);
}

#dice .cover.x {
    transform: rotateY(90deg);
}

#dice .cover.z {
    transform: rotateX(90deg);
}

#dice .front {
    transform: translateZ(30px);
}

#dice .front.inner {
    transform: translateZ(28px);
}

#dice .back {
    transform: rotateX(-180deg) translateZ(30px);
}

#dice .back.inner {
    transform: rotateX(-180deg) translateZ(28px);
}

#dice .right {
    transform: rotateY(90deg) translateZ(30px);
}

#dice .right.inner {
    transform: rotateY(90deg) translateZ(28px);
}

#dice .left {
    transform: rotateY(-90deg) translateZ(30px);
}

#dice .left.inner {
    transform: rotateY(-90deg) translateZ(28px);
}

#dice .top {
    transform: rotateX(90deg) translateZ(30px);
}

#dice .top.inner {
    transform: rotateX(90deg) translateZ(28px);
}

#dice .bottom {
    transform: rotateX(-90deg) translateZ(30px);
}

#dice .bottom.inner {
    transform: rotateX(-90deg) translateZ(28px);
}
.dot {
    background: #444;
    border-radius: 7px;
    box-shadow: inset 5px 0 10px #000;
    height: 14px;
    position: absolute;
    width: 14px;
}

.dot.center {
    margin: 23px 0 0 23px;
}

.dot.dtop {
    margin-top: 6px;
}

.dot.dleft {
    margin-left: 40px;
}

.dot.dright {
    margin-left: 6px;
}

.dot.dbottom {
    margin-top: 40px;
}

.dot.center.dleft {
    margin: 23px 0 0 6px;
}

.dot.center.dright {
    margin: 23px 0 0 40px;
}

@keyframes spin {
    0% {
        transform: translateZ(-100px) rotateX(0deg) rotateY(0deg) rotate(0deg);
    }

    16% {
        transform: translateZ(-100px) rotateX(180deg) rotateY(180deg) rotate(0deg);
    }

    33% {
        transform: translateZ(-100px) rotateX(1turn) rotateY(90deg) rotate(180deg);
    }

    50% {
        transform: translateZ(-100px) rotateX(1turn) rotateY(1turn) rotate(1turn);
    }

    66% {
        transform: translateZ(-100px) rotateX(180deg) rotateY(1turn) rotate(270deg);
    }

    83% {
        transform: translateZ(-100px) rotateX(270deg) rotateY(180deg) rotate(180deg);
    }

    to {
        transform: translateZ(-100px) rotateX(1turn) rotateY(1turn) rotate(1turn);
    }
}
</style>

